import React from "react";
import { nameof } from "../../store/services/metadataService";
import * as Metadata from '../../entities/Metadata';
import { DisplayFieldService } from "../common/DisplayFieldService";
import { Dictionary, EntityType } from '../../entities/common';
import {
    extractKeyResult, IObjectiveAttrs, isObjective, ObjectiveCalculationType, ObjectiveOrKeyResult, OKRCalculationTypeOptions, OKRDirection, OKRValueType
} from "../../store/ObjectivesListStore";
import { formatsMap, OkrProgressFormatter, ProgressFormatter, propsOKRFlagLabels } from "../common/formatters/OkrFormatters";
import NumberInput from '../common/inputs/NumberInput';
import { NumberFlagInput } from '../common/inputs/NumberFlagInput';
import { IInputProps } from "../common/interfaces/IInputProps";
import { IValidator, Validator } from "../../validation";
import DropdownInput from "../common/inputs/DropdownInput";
import { rendersBuilder as commonRendersBuilder, validators } from '../field/Fields';
import { FlagEditor } from "../common/inputs/FlagEditor";
import { Status_ } from "../../store/MyWorkStore";
import { keyResultValidators } from "./KeyResultEdit";

export const rendersBuilder = () => ({
    ...commonRendersBuilder(),
    [nameof<IObjectiveAttrs>("ValueType")]: (props: IInputProps, state: { attributes: IObjectiveAttrs }, field: Metadata.Field): JSX.Element | null =>
        DisplayFieldService.buildFieldInputElement(field, {
            ...props,
            onChanged: props.onChanged ? (v) => {
                let extra: Dictionary<any> | undefined;

                if (v === OKRValueType.Percent) {
                    if (!state.attributes.StartValue && !state.attributes.TargetValue && !state.attributes.CurrentValue) {
                        extra = {
                            [nameof<IObjectiveAttrs>("StartValue")]: 0,
                            [nameof<IObjectiveAttrs>("CurrentValue")]: 0,
                            [nameof<IObjectiveAttrs>("TargetValue")]: 100
                        };
                    }
                } else if (v === OKRValueType.Flag) {
                    extra = {
                        [nameof<IObjectiveAttrs>("StartValue")]: 0,
                        [nameof<IObjectiveAttrs>("CurrentValue")]: 0,
                        [nameof<IObjectiveAttrs>("TargetValue")]: 1,
                        [nameof<IObjectiveAttrs>("Direction")]: OKRDirection.Growth
                    };
                }

                props.onChanged!(v, extra);
            } : undefined,
            onEditComplete: (v, extra) => {
                if (v === OKRValueType.Percent) {
                    if (!state.attributes.StartValue && !state.attributes.TargetValue && !state.attributes.CurrentValue) {
                        extra = extra ?? {};
                        extra[nameof<IObjectiveAttrs>("StartValue")] = 0;
                        extra[nameof<IObjectiveAttrs>("CurrentValue")] = 0;
                        extra[nameof<IObjectiveAttrs>("TargetValue")] = 100;
                    }
                } else if (v === OKRValueType.Flag) {
                    extra = extra ?? {};
                    extra[nameof<IObjectiveAttrs>("StartValue")] = 0;
                    extra[nameof<IObjectiveAttrs>("CurrentValue")] = 0;
                    extra[nameof<IObjectiveAttrs>("TargetValue")] = 1;
                    extra[nameof<IObjectiveAttrs>("Direction")] = OKRDirection.Growth;
                }

                props.onEditComplete(v, extra);
            }
        }, EntityType.Objective),
    [nameof<IObjectiveAttrs>("Direction")]: (props: IInputProps, state: { attributes: IObjectiveAttrs }, field: Metadata.Field): JSX.Element | null =>
        (state.attributes.CalculationType === ObjectiveCalculationType.Calculated && state.attributes.ValueType !== OKRValueType.Flag)
            ? DisplayFieldService.buildFieldInputElement(field, props, EntityType.Objective)
            : null,
    [nameof<IObjectiveAttrs>("StartValue")]: (props: IInputProps, state: { attributes: IObjectiveAttrs }, field: Metadata.Field): JSX.Element | null =>
        state.attributes.ValueType !== OKRValueType.Flag
            ? <NumberInput {...props} inputProps={{ readOnly: field.isReadonly }} format={formatsMap[state.attributes.ValueType]} validator={DisplayFieldService.buildValidatorFromField(field)} />
            : null,
    [nameof<IObjectiveAttrs>("TargetValue")]: (props: IInputProps, state: { attributes: IObjectiveAttrs }, field: Metadata.Field): JSX.Element | null =>
        state.attributes.ValueType !== OKRValueType.Flag
            ? <NumberInput {...props} inputProps={{ readOnly: field.isReadonly }} format={formatsMap[state.attributes.ValueType]} validator={DisplayFieldService.buildValidatorFromField(field)} />
            : null,
    [nameof<IObjectiveAttrs>("CurrentValue")]: (props: IInputProps, state: { attributes: IObjectiveAttrs }, field: Metadata.Field): JSX.Element | null =>
        state.attributes.ValueType !== OKRValueType.Flag
            ? <NumberInput {...props} inputProps={{ readOnly: field.isReadonly }} format={formatsMap[state.attributes.ValueType]} validator={DisplayFieldService.buildValidatorFromField(field)} />
            : <NumberFlagInput {...props} {...propsOKRFlagLabels} inputProps={{ readOnly: field.isReadonly }} value={state.attributes.CurrentValue} />,
    [nameof<IObjectiveAttrs>("Progress")]: (props: IInputProps, state: { attributes: IObjectiveAttrs }, field: Metadata.Field): JSX.Element | null =>
        state.attributes.CalculationType === ObjectiveCalculationType.Calculated
            ? <OkrProgressFormatter direction={state.attributes.Direction} startValue={state.attributes.StartValue}
                targetValue={state.attributes.TargetValue} currentValue={state.attributes.CurrentValue} />
            : state.attributes.CalculationType === ObjectiveCalculationType.KR_Rollup
                ? <ProgressFormatter value={state.attributes.Progress} />
                : DisplayFieldService.buildFieldInputElement(field, props, EntityType.Objective)
})

export const rendersInlineBuilder = () => ({
    ...commonRendersBuilder(),
    [nameof<IObjectiveAttrs>("ValueType")]: (props: IInputProps, state: ObjectiveOrKeyResult, field: Metadata.Field, validator?: Validator): JSX.Element | null =>
        DisplayFieldService.buildGridEditorFieldInputElement(field, {
            ...props,
            onChanged: props.onChanged ? (v) => {
                let extra: Dictionary<any> | undefined;

                if (v === OKRValueType.Percent) {
                    if (!state.attributes.StartValue && !state.attributes.TargetValue && !state.attributes.CurrentValue) {
                        extra = {
                            [nameof<IObjectiveAttrs>("StartValue")]: 0,
                            [nameof<IObjectiveAttrs>("CurrentValue")]: 0,
                            [nameof<IObjectiveAttrs>("TargetValue")]: 100
                        };
                    }
                } else if (v === OKRValueType.Flag) {
                    extra = {
                        [nameof<IObjectiveAttrs>("StartValue")]: 0,
                        [nameof<IObjectiveAttrs>("CurrentValue")]: 0,
                        [nameof<IObjectiveAttrs>("TargetValue")]: 1,
                        [nameof<IObjectiveAttrs>("Direction")]: OKRDirection.Growth
                    };
                }

                props.onChanged!(v, extra);
            } : undefined,
            onEditComplete: (v, extra) => {
                if (v === OKRValueType.Percent) {
                    if (!state.attributes.StartValue && !state.attributes.TargetValue && !state.attributes.CurrentValue) {
                        extra = extra ?? {};
                        extra[nameof<IObjectiveAttrs>("StartValue")] = 0;
                        extra[nameof<IObjectiveAttrs>("CurrentValue")] = 0;
                        extra[nameof<IObjectiveAttrs>("TargetValue")] = 100;
                    }
                } else if (v === OKRValueType.Flag) {
                    extra = extra ?? {};
                    extra[nameof<IObjectiveAttrs>("StartValue")] = 0;
                    extra[nameof<IObjectiveAttrs>("CurrentValue")] = 0;
                    extra[nameof<IObjectiveAttrs>("TargetValue")] = 1;
                    extra[nameof<IObjectiveAttrs>("Direction")] = OKRDirection.Growth;
                }

                props.onEditComplete(v, extra);
            }
        }, EntityType.Objective),
    [nameof<IObjectiveAttrs>("Direction")]: (props: IInputProps, state: ObjectiveOrKeyResult, field: Metadata.Field, validator?: Validator): JSX.Element | null =>
    {
        if (isObjective(state)) {
            return state.attributes.CalculationType === ObjectiveCalculationType.Calculated && state.attributes.ValueType !== OKRValueType.Flag
                ? DisplayFieldService.buildGridEditorFieldInputElement(field, props, EntityType.Objective, undefined, undefined, validator)
                : <></>;
        }

        const keyresult = extractKeyResult(state)
        return keyresult?.calculationType === ObjectiveCalculationType.Calculated && keyresult.valueType !== OKRValueType.Flag
            ? DisplayFieldService.buildGridEditorFieldInputElement(field, props, EntityType.KeyResult, undefined, undefined, validator)
            : <></>;
    },
    [nameof<IObjectiveAttrs>("StartValue")]: (props: IInputProps, state: ObjectiveOrKeyResult, field: Metadata.Field, validator?: Validator): JSX.Element | null =>
        state.attributes.ValueType !== OKRValueType.Flag
            ? <NumberInput {...props} inputProps={{ readOnly: field.isReadonly }} format={formatsMap[state.attributes.ValueType]} validator={validator} />
            : null,
    [nameof<IObjectiveAttrs>("TargetValue")]: (props: IInputProps, state: ObjectiveOrKeyResult, field: Metadata.Field, validator?: Validator): JSX.Element | null =>
        state.attributes.ValueType !== OKRValueType.Flag
            ? <NumberInput {...props} inputProps={{ readOnly: field.isReadonly }} format={formatsMap[state.attributes.ValueType]} validator={validator} />
            : null,
    [nameof<IObjectiveAttrs>("CurrentValue")]: (props: IInputProps, state: ObjectiveOrKeyResult, field: Metadata.Field, validator?: Validator): JSX.Element | null =>
        state.attributes.ValueType !== OKRValueType.Flag
            ? <NumberInput {...props} inputProps={{ readOnly: field.isReadonly }} format={formatsMap[state.attributes.ValueType]} validator={validator} />
            : <FlagEditor {...props} readOnly={field.isReadonly}
                onChanged={(value: boolean) => props.onChanged?.(value ? 1 : 0)}
                onEditComplete={(value: boolean) => props.onEditComplete?.(value ? 1 : 0)}
            />,
    [nameof<IObjectiveAttrs>("Progress")]: (props: IInputProps, state: ObjectiveOrKeyResult, field: Metadata.Field, validator?: Validator): JSX.Element | null =>
        state.attributes.CalculationType === ObjectiveCalculationType.Calculated
            ? <OkrProgressFormatter direction={state.attributes.Direction} startValue={state.attributes.StartValue}
                targetValue={state.attributes.TargetValue} currentValue={state.attributes.CurrentValue} />
            : state.attributes.CalculationType === ObjectiveCalculationType.KR_Rollup
                ? <ProgressFormatter value={state.attributes.Progress} />
                : DisplayFieldService.buildGridEditorFieldInputElement(field, props, EntityType.Objective, undefined, undefined, validator),
    [nameof<IObjectiveAttrs>("Status")]: (props: IInputProps, state: ObjectiveOrKeyResult, field: Metadata.Field, validator?: Validator): JSX.Element | null =>
        isObjective(state)
            ? DisplayFieldService.buildGridEditorFieldInputElement(field, props, EntityType.Objective, undefined, undefined, validator)
            : DisplayFieldService.buildGridEditorFieldInputElement(field, props, EntityType.KeyResult, undefined, undefined, validator),
    [nameof<IObjectiveAttrs>("CalculationType")]: (props: IInputProps, state: ObjectiveOrKeyResult, field: Metadata.Field, validator?: Validator): JSX.Element | null => {
        return isObjective(state)
            ? DisplayFieldService.buildGridEditorFieldInputElement(field, props, EntityType.Objective, undefined, undefined, validator)
            : <DropdownInput {...props}
                value={extractKeyResult(state)?.calculationType}
                inputProps={{
                    options: OKRCalculationTypeOptions,
                    readOnly: field.isReadonly
                }}
                validator={validator} 
                hideCaretDown />
    },
    [nameof<IObjectiveAttrs>("Status")]: (props: IInputProps, state: ObjectiveOrKeyResult, field: Metadata.Field, validator?: Validator): JSX.Element | null => {
        return isObjective(state)
            ? DisplayFieldService.buildGridEditorFieldInputElement(field, props, EntityType.Objective, undefined, undefined, validator)
            : DisplayFieldService.buildGridEditorFieldInputElement(field, {
                ...props,
                onChanged: (value: string) => props.onChanged?.(Status_.getStatusCategory(value)),
                onEditComplete: (value: string) => props.onEditComplete?.(Status_.getStatusCategory(value))
            }, EntityType.Objective, undefined, undefined, validator)
    },
    [nameof<IObjectiveAttrs>("Description")]: (props: IInputProps, state: ObjectiveOrKeyResult, field: Metadata.Field, validator?: Validator): JSX.Element | null => {
        if (isObjective(state)) {
            return DisplayFieldService.buildGridEditorFieldInputElement(field, props, EntityType.Objective, undefined, undefined, validator);
        }

        const keyresult = extractKeyResult(state)
        return DisplayFieldService.buildGridEditorFieldInputElement(field, {
            ...props,
            value: keyresult?.description
        }, EntityType.KeyResult, undefined, undefined, validator);
    }
});

export const inlineValidatorsBuilder = () => {
    return {
        ...validators,
        [nameof<IObjectiveAttrs>("CalculationType")]: (state: ObjectiveOrKeyResult, field: Metadata.Field): IValidator => {
            return isObjective(state)
                ? DisplayFieldService.getValidatorBuilder(field).build()
                : Validator.new().build();
        },
        [nameof<IObjectiveAttrs>("Progress")]: (state: ObjectiveOrKeyResult, field: Metadata.Field): IValidator => {
            return isObjective(state)
                ? DisplayFieldService.getValidatorBuilder(field).build()
                : keyResultValidators.progress;
        },
        [nameof<IObjectiveAttrs>("StartValue")]: (state: ObjectiveOrKeyResult, field: Metadata.Field): IValidator => {
            return isObjective(state)
                ? DisplayFieldService.getValidatorBuilder(field).build()
                : keyResultValidators.startValue;
        },
        [nameof<IObjectiveAttrs>("CurrentValue")]: (state: ObjectiveOrKeyResult, field: Metadata.Field): IValidator => {
            return isObjective(state)
                ? DisplayFieldService.getValidatorBuilder(field).build()
                : keyResultValidators.currentValue;
        },
        [nameof<IObjectiveAttrs>("TargetValue")]: (state: ObjectiveOrKeyResult, field: Metadata.Field): IValidator => {
            return isObjective(state)
                ? DisplayFieldService.getValidatorBuilder(field).build()
                : keyResultValidators.targetValue;
        },
    }
}
